import type { BiomeId } from './types';
import type { CollisionShape, Point, Rect, RectShape } from './geometry';

export const CONTINUOUS_WORLD_SIZE = { width: 5120, height: 3840 } as const;

export interface ContinuousBiomeZone {
  id: string;
  biome: BiomeId;
  area: Rect;
}

export interface FoundationChunk {
  id: string;
  asset: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WorldPropPlacement {
  id: string;
  asset: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sortY: number;
  layer: 'props' | 'foreground';
  /** Collision coordinates are local to the center-bottom prop anchor. */
  collision: readonly CollisionShape[];
}

export interface BridgeCorridor {
  id: string;
  area: Rect;
}

export interface ContinuousWorldDefinition {
  id: string;
  bounds: Rect;
  biomeHysteresis: number;
  biomeZones: readonly ContinuousBiomeZone[];
  foundationChunks: readonly FoundationChunk[];
  props: readonly WorldPropPlacement[];
  terrainBlockers: readonly CollisionShape[];
  bridgeCorridors: readonly BridgeCorridor[];
}

function expanded(rect: Rect, amount: number): Rect {
  return {
    x: rect.x - amount,
    y: rect.y - amount,
    w: rect.w + amount * 2,
    h: rect.h + amount * 2,
  };
}

function contains(rect: Rect, point: Point): boolean {
  return point.x >= rect.x && point.x < rect.x + rect.w && point.y >= rect.y && point.y < rect.y + rect.h;
}

export function biomeAtWorldPoint(
  world: ContinuousWorldDefinition,
  point: Point,
  previous?: BiomeId,
): BiomeId {
  const previousZone = previous
    ? world.biomeZones.find(({ biome }) => biome === previous)
    : undefined;
  if (previousZone && contains(expanded(previousZone.area, world.biomeHysteresis), point)) {
    return previousZone.biome;
  }
  return world.biomeZones.find(({ area }) => contains(area, point))?.biome ?? 'meadow';
}

function translateShape(shape: CollisionShape, prop: WorldPropPlacement): CollisionShape {
  const id = `prop:${prop.id}:${shape.id}`;
  if (shape.kind === 'rect') {
    return { ...shape, id, x: prop.x + shape.x, y: prop.y + shape.y };
  }
  return {
    ...shape,
    id,
    points: shape.points.map((point) => ({ x: prop.x + point.x, y: prop.y + point.y })),
  };
}

function intersection(a: Rect, b: Rect): Rect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.w, b.x + b.w);
  const bottom = Math.min(a.y + a.h, b.y + b.h);
  return right > x && bottom > y ? { x, y, w: right - x, h: bottom - y } : null;
}

function subtractRect(source: RectShape, cut: Rect, suffix: string): RectShape[] {
  const overlap = intersection(source, cut);
  if (!overlap) return [source];
  const pieces: RectShape[] = [];
  const add = (id: string, x: number, y: number, w: number, h: number): void => {
    if (w > 0 && h > 0) pieces.push({ kind: 'rect', id: `${source.id}:${suffix}:${id}`, x, y, w, h });
  };
  add('top', source.x, source.y, source.w, overlap.y - source.y);
  add('bottom', source.x, overlap.y + overlap.h, source.w, source.y + source.h - overlap.y - overlap.h);
  add('left', source.x, overlap.y, overlap.x - source.x, overlap.h);
  add('right', overlap.x + overlap.w, overlap.y, source.x + source.w - overlap.x - overlap.w, overlap.h);
  return pieces;
}

export function blockersForContinuousWorld(world: ContinuousWorldDefinition): CollisionShape[] {
  const terrain = world.terrainBlockers.reduce<CollisionShape[]>((all, shape) => {
    const prefixed: CollisionShape = { ...shape, id: `terrain:${shape.id}` };
    if (prefixed.kind === 'polygon') return [...all, prefixed];
    const pieces = world.bridgeCorridors.reduce<RectShape[]>(
      (pieces, corridor) => pieces.flatMap((piece) => subtractRect(piece, corridor.area, corridor.id)),
      [prefixed],
    );
    return [...all, ...pieces];
  }, []);
  const props = world.props.flatMap((prop) => prop.collision.map((shape) => translateShape(shape, prop)));
  return [...terrain, ...props];
}
